/**
 * Projekte und Suche auf /runs (seit 03.11.2026, Entwurf B „Projekte als
 * Reiter“ im Canvas „99takes Runs-Filter“, gewählt von Robert).
 *
 * Ein Run gehört zum Projekt seiner Studie (sim_studies.projekt, projekt.ts
 * am Server). Runs ohne Studie oder mit einer Studie ohne Projekt stehen unter
 * „Ohne Projekt“. Gleich geschriebene Namen ohne Groß- und Kleinschreibung
 * sind ein Projekt; der Server gleicht sie beim Setzen schon an.
 */

import type { Lauf } from './api';

/** Auswahl in der Projektspalte. */
export type ProjektWahl = { art: 'alle' } | { art: 'ohne' } | { art: 'projekt'; name: string };

export interface ProjektEintrag {
  name: string;
  runs: number;
}

export interface ProjektUebersicht {
  projekte: ProjektEintrag[];
  alle: number;
  ohne: number;
}

const schluessel = (s: string) => s.toLocaleLowerCase('de');

export function projektVon(l: Lauf): string | null {
  const p = l.projekt?.trim();
  return p ? p : null;
}

/** Projekte mit der Zahl ihrer Runs, das zuletzt benutzte zuerst. */
export function projekteAus(laeufe: Lauf[]): ProjektUebersicht {
  const je = new Map<string, { name: string; runs: number; zuletzt: string }>();
  let ohne = 0;
  for (const l of laeufe) {
    const p = projektVon(l);
    if (!p) { ohne++; continue; }
    const k = schluessel(p);
    const e = je.get(k);
    if (e) {
      e.runs++;
      if (l.created_at > e.zuletzt) e.zuletzt = l.created_at;
    } else je.set(k, { name: p, runs: 1, zuletzt: l.created_at });
  }
  const projekte = [...je.values()]
    .sort((a, b) => b.zuletzt.localeCompare(a.zuletzt) || a.name.localeCompare(b.name, 'de'))
    .map(({ name, runs }) => ({ name, runs }));
  return { projekte, alle: laeufe.length, ohne };
}

/** Normalisiert die Suche: Kleinbuchstaben, einfache Leerzeichen. */
function suchwoerter(suche: string): string[] {
  return schluessel(suche).split(/\s+/).filter(Boolean);
}

/**
 * Passt ein Run zu Auswahl und Suche? Gesucht wird in Studie, Projekt, Text,
 * Fassungsname und Zielgruppe; jedes Wort muss irgendwo vorkommen.
 */
export function passt(l: Lauf, wahl: ProjektWahl, suche: string): boolean {
  const p = projektVon(l);
  if (wahl.art === 'ohne' && p) return false;
  if (wahl.art === 'projekt' && (!p || schluessel(p) !== schluessel(wahl.name))) return false;
  const woerter = suchwoerter(suche);
  if (!woerter.length) return true;
  const heu = schluessel([l.study, p, l.text, l.variant, l.set_name, l.set].filter(Boolean).join(' '));
  return woerter.every(w => heu.includes(w));
}

export function filtere(laeufe: Lauf[], wahl: ProjektWahl, suche: string): Lauf[] {
  return laeufe.filter(l => passt(l, wahl, suche));
}

/** Ist das gewählte Projekt noch da (etwa nach einem Neuladen)? Sonst „Alle“. */
export function gueltigeWahl(wahl: ProjektWahl, u: ProjektUebersicht): ProjektWahl {
  if (wahl.art !== 'projekt') return wahl;
  const treffer = u.projekte.find(p => schluessel(p.name) === schluessel(wahl.name));
  return treffer ? { art: 'projekt', name: treffer.name } : { art: 'alle' };
}

/** Für die Adresse: ?projekt=Everto, ?projekt=- für „Ohne Projekt“. */
export function wahlAusParam(v: string | null): ProjektWahl {
  if (v === null || v === '') return { art: 'alle' };
  if (v === '-') return { art: 'ohne' };
  return { art: 'projekt', name: v };
}

export function wahlAlsParam(w: ProjektWahl): string | null {
  return w.art === 'alle' ? null : w.art === 'ohne' ? '-' : w.name;
}

/** Studien ohne Projekt, die in der Liste vorkommen: die kann man zuordnen. */
export function studienOhneProjekt(laeufe: Lauf[]): Array<{ id: string; name: string }> {
  const je = new Map<string, string>();
  for (const l of laeufe) if (l.study_id && l.study && !projektVon(l)) je.set(l.study_id, l.study);
  return [...je].map(([id, name]) => ({ id, name }));
}
